import Button from '@allesauto/core/button';
import Headline from '@allesauto/core/headline';
import TextLink from '@allesauto/core/textLink';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import error from '@allesauto/design/animations/error.json';
import success from '@allesauto/design/animations/success.json';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { HeadlineVariation } from '@allesauto/utils/consts';
import { isWhiteLabel } from '@allesauto/utils/funcs';
import { WatsonHealthRotate_18032 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import CreditAssessmentOverlay from '@/components/forms/PreApprovalForm/CreditAssessmentOverlay';

import { DynamicAnimation } from '@/global/dynamicComponents/DynamicAnimation';
import { openOverlay } from '@/store/redux/actions/app';

export interface OverlaySuccessCreditRatingProps {
  onBtnClick: () => void;
  creditRatingFailed?: boolean;
  vehicleCartItem: SchemaTypes.CartItem;
  setStatusPreapprovalPassed?: React.Dispatch<
    React.SetStateAction<undefined | boolean>
  >;
  setBackToPdpLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  setFormStatus?: React.Dispatch<
    React.SetStateAction<{ [key: string]: string } | undefined>
  >;
  statusPreapprovalPassed?: boolean | undefined;
}

const OverlaySuccessCreditRating: FC<
  React.PropsWithChildren<OverlaySuccessCreditRatingProps>
> = ({
  vehicleCartItem,
  onBtnClick,
  creditRatingFailed = false,
  setStatusPreapprovalPassed,
  setBackToPdpLoading,
  setFormStatus,
  statusPreapprovalPassed,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const whitelabel = useWhiteLabel();
  const className = {
    bannerContainer: clsx(
      'flex flex-col justify-between pb-20px pt-60px px-15px md:flex-row md:p-60px',
      'monochrome:bg-grey10'
    ),
    bannerWrapper: 'flex flex-col align-middle',
    bannerHeadline: 'font-headline text-left text-headline2 text-purple80',
    bottomText: clsx('block font-weight-normal mb-15px mt-60px'),
  };

  const creditAssessmentProps = {
    children: (
      <CreditAssessmentOverlay
        vehicleCartItem={vehicleCartItem}
        setStatusPreapprovalPassed={setStatusPreapprovalPassed}
        setBackToPdpLoading={setBackToPdpLoading}
        setFormStatus={setFormStatus}
        statusPreapprovalPassed={statusPreapprovalPassed}
      />
    ),
    title: translate(
      intl,
      'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_OVERLAY_HEADING'
    ),
    creditAssessmentOverlay: true,
    withPaddings: false,
  };

  return (
    <div>
      <div
        className={className.bannerContainer}
        style={{
          border: creditRatingFailed
            ? '3px solid red'
            : '3px solid rgb(26, 229, 185)',
        }}
      >
        {creditRatingFailed ? (
          <div className={className.bannerWrapper}>
            <DynamicAnimation
              width={100}
              height={100}
              autoplay={true}
              file={error}
            />
            <Headline
              headlineText={translate(
                intl,
                isWhiteLabel(whitelabel)
                  ? 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_FAIL_HEADLINE_WL'
                  : 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_FAIL_HEADLINE'
              )}
              headlineTag={HeadlineVariation.H3}
              descriptionColor="text-grey100"
              className="mt-[24px] text-center"
            />
            <TextLink
              text="Erneut durchführen"
              icon={<WatsonHealthRotate_18032 className="h-20px w-20px" />}
              onClick={() => {
                dispatch(openOverlay(creditAssessmentProps));
              }}
              mobileBlockVariant={true}
              customClassName="rounded-[0px] mt-[24px]"
            />
          </div>
        ) : (
          <div className={className.bannerWrapper}>
            <DynamicAnimation
              width={100}
              height={100}
              autoplay={true}
              file={success}
            />
            <Headline
              headlineText={translate(
                intl,
                isWhiteLabel(whitelabel)
                  ? 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_SUCCESS_HEADLINE_WL'
                  : 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_SUCCESS_HEADLINE'
              )}
              headlineTag={HeadlineVariation.H3}
              descriptionColor="text-grey100"
              className="mt-[24px] text-center"
            />
          </div>
        )}
      </div>
      {creditRatingFailed ? (
        <>
          <div className={className.bannerWrapper}>
            <Headline
              headlineText={translate(
                intl,
                'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_CONTACT_US'
              )}
              headlineTag={HeadlineVariation.H5}
              descriptionColor="text-grey100"
              className="bg-white mt-[40px] pb-[32px] pl-[60px] pr-[60px] pt-[32px]"
              descriptionTextPreApprovalForm={translate(
                intl,
                'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_CONTACT_US_DESCRIPTION'
              )}
            />
          </div>
          <Button
            text={translate(
              intl,
              'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_FAILED_CONTACT_BUTTON'
            )}
            onClick={onBtnClick}
            customClassName={'mt-[80px] mb-[40px]'}
            fullWidth
          />
        </>
      ) : (
        <Button
          text={
            creditRatingFailed
              ? translate(
                  intl,
                  'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_FAILED_CONTACT_BUTTON'
                )
              : translate(
                  intl,
                  'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_SUCCESS_BUTTON'
                )
          }
          onClick={onBtnClick}
          customClassName={
            creditRatingFailed ? 'mt-[80px] mb-[40px]' : 'mt-[200px] mb-[40px]'
          }
          fullWidth
        />
      )}
    </div>
  );
};
export default OverlaySuccessCreditRating;
